import './api/interceptors';
import type { Experiments } from '@carvana/experiment/SharedTypes';
import isPropValid from '@emotion/is-prop-valid';
import { DehydratedState, Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState, useCallback } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { v4 } from 'uuid';

import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import AuthProvider from './context/AuthProvider';
import CvtExperimentProvider from './experiments/CvtExperimentProvider';
import mfeConfig from './mfeConfig';
import Cookie from './shared/Cookie';
import { isIOSWebView } from './shared/nativeAppMessaging';
import { extractiOSInstallationID } from './shared/urlParams';

const Header = dynamic(() => import('./components/Header'));
const Footer = dynamic(() => import('./components/Footer'));

mfeConfig();

// styled-components v6 forwards every prop to the DOM by default. Restore the
// v5 behavior of filtering non-DOM-valid props so helios components (which
// pre-date our v6 upgrade) stop leaking `priority`, `expanded`, etc. to the DOM.
const shouldForwardProp = (propName: string, target: unknown) =>
  typeof target === 'string' ? isPropValid(propName) : true;

const DisplayController = styled.div`
  display: none;

  .content-loaded &:not(.transposeContent) {
    display: block;
  }

  &.transposeContent {
    display: none;
  }

  @media print {
    display: none;
  }
`;

const AppProviders = ({
  children,
  cookies,
  pageProps
}: {
  children: ReactElement;
  cookies: string;
  pageProps: {
    dehydratedState: DehydratedState;
    experiments?: Experiments;
    browserCookieId?: string;
  };
}) => {
  const { dehydratedState, experiments = {} } = pageProps;
  const [queryClient] = useState(() => new QueryClient());
  const router = useRouter();
  const [transposeContent, setTransposeContent] = useState(false);

  useEffect(() => {
    import('./shared/datadog').then(module => {
      module.initializeDataDog(experiments);
    });
  }, []);

  const isIosNativeApp = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return isIOSWebView();
  }, [router.pathname]);

  useEffect(() => {
    if (router.query.installationID && typeof window !== 'undefined') {
      extractiOSInstallationID(router.query, true);
    }
  }, [router.query.installationID]);

  useEffect(() => {
    const baseUrlsToTranspose = ['/get-value', '/edit'];
    const urlsToTranspose = isIosNativeApp()
      ? [...baseUrlsToTranspose, '/vehicle']
      : baseUrlsToTranspose;

    const shouldTranspose = urlsToTranspose.some(url => router.pathname.includes(url));
    setTransposeContent(shouldTranspose);

    const browserCookie = Cookie.getBcId();
    const dev = process.env.NODE_ENV === 'development';
    if (dev && !browserCookie) {
      Cookie.setCookie('BrowserCookieId', v4(), 30);
    }

    if (typeof window !== 'undefined') {
      if (shouldTranspose) {
        document.body.classList.add('content-loaded');
      } else {
        const timer = setTimeout(() => {
          document.body.classList.add('content-loaded');
        }, 50);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [router.pathname, router.isReady, isIosNativeApp]);

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ErrorBoundary>
        <CvtExperimentProvider preloadedExperiments={experiments}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools />
            <DisplayController className={classnames({ transposeContent })}>
              <Header cookies={cookies} />
            </DisplayController>
            <AuthProvider>
              <Hydrate state={dehydratedState}>{children}</Hydrate>
            </AuthProvider>
            <DisplayController className={classnames({ transposeContent })}>
              <Footer />
            </DisplayController>
          </QueryClientProvider>
        </CvtExperimentProvider>
      </ErrorBoundary>
    </StyleSheetManager>
  );
};

export default AppProviders;
